import { getValidationErrors } from "pages/gdt/cell-renderers/CellRendererBase.helpers";
import { ModalConfirmStartTasks } from "pages/gdt/modals/ModalConfirmStartTasks";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useState } from "react";
import { useExecuteAnalysisTableRows } from "./useExecuteAnalysisTableRows";
import { DATA_TABLE_TOOLTIP_ID } from "../DataTableTooltip";
import { ButtonIconPlainPermissioned } from "pages/gdt/plain/ButtonIconPlain";
import { ToolStatus } from "types/constants";
import { GridApi } from "ag-grid-community";
import { DataTableRowData } from "../DataTable";

interface QuickActionRunRowBaseProps {
  tableId: string;
  rowIds: string[];
  tooltipContent: string[];
  isDisabled: boolean;
  id: string;
  requireConfirmation: boolean;
  gridApi: GridApi<DataTableRowData> | undefined;
}

const QuickActionRunRowBase = ({
  tableId,
  rowIds,
  tooltipContent,
  isDisabled,
  id,
  requireConfirmation,
  gridApi,
}: QuickActionRunRowBaseProps) => {
  const executeAnalysisTableRows = useExecuteAnalysisTableRows(gridApi);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const startTasks = async () => {
    setIsLoading(true);
    await executeAnalysisTableRows(rowIds, tableId);
    setIsLoading(false);
  };

  const onCancel = () => {
    setShowConfirmModal(false);
  };

  const onConfirm = () => {
    void startTasks();
    setShowConfirmModal(false);
  };

  return (
    <>
      <ButtonIconPlainPermissioned
        requiredPermission="execute"
        aria-label={tooltipContent?.toString()}
        data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
        data-tooltip-html={tooltipContent.join("<br />")}
        color="primary"
        iconSize="m"
        iconType="playFilled"
        onClick={() => {
          if (requireConfirmation) {
            setShowConfirmModal(true);
          } else {
            onConfirm();
          }
        }}
        disabled={isDisabled}
        isLoading={isLoading}
        id={id}
      />
      {showConfirmModal && (
        <ModalConfirmStartTasks
          onCancel={onCancel}
          onConfirm={onConfirm}
          numberOfTasks={rowIds.length}
        />
      )}
    </>
  );
};

interface QuickActionRunRowProps {
  tableId: string;
  rowId: string;
  gridApi: GridApi<DataTableRowData> | undefined;
  readOnly?: boolean;
}

export const QuickActionRunRow = ({
  tableId,
  rowId,
  gridApi,
  readOnly,
}: QuickActionRunRowProps) => {
  const {
    invalidColumns,
    isDisabled,
    isToolVersionDeprecated,
    isToolVersionAccessible,
  } = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    const row = table?.rows.find(({ id }) => id === rowId);

    if (table === undefined || row === undefined) {
      return {
        invalidColumns: [],
        isDisabled: false,
        isToolVersionDeprecated: false,
        isToolVersionAccessible: true,
      };
    }

    if (!row.toolVersion) {
      return {
        invalidColumns: [],
        isDisabled: true,
        isToolVersionDeprecated: false,
        isToolVersionAccessible: false,
      };
    }
    if (row.toolVersion.status === ToolStatus.DEPRECATED) {
      return {
        invalidColumns: [],
        isDisabled: true,
        isToolVersionDeprecated: true,
        isToolVersionAccessible: true,
      };
    }

    const invalidColumns: string[] = [];
    let isDisabled = false;

    row.cells.forEach((cell, colIdx) => {
      const column = table.columns[colIdx];
      const errors = getValidationErrors({
        tableKind: table.kind,
        columnDefinition: column.definition,
        isColumnRequired: column.required,
        value: cell.value,
      });

      if (errors.length > 0) {
        invalidColumns.push(column.name);
      }

      // Disable execution if row contains a blocking validation error
      if (errors.some((e) => e.isBlocking)) {
        isDisabled = true;
      }
    });

    return {
      invalidColumns,
      isDisabled,
      isToolVersionDeprecated: false,
      isToolVersionAccessible: true,
    };
  });

  const tooltipContent = (() => {
    if (invalidColumns.length > 0) {
      return ["Invalid entries", ...invalidColumns.map((name) => `• ${name}`)];
    } else if (!isToolVersionAccessible) {
      return ["You do not have permission to execute this tool version"];
    } else if (isToolVersionDeprecated) {
      return ["Tool version is deprecated and cannot be executed"];
    } else {
      return ["Run row"];
    }
  })();

  return (
    <QuickActionRunRowBase
      gridApi={gridApi}
      tooltipContent={tooltipContent}
      tableId={tableId}
      rowIds={[rowId]}
      isDisabled={isDisabled || (readOnly ?? false)}
      id={"quickActionRunRow"}
      requireConfirmation={false}
    />
  );
};

export interface QuickActionRunSelectedRowsProps {
  gridApi: GridApi<DataTableRowData> | undefined;
  selectedRowIds: string[];
  tableId: string;
}

export const QuickActionRunSelectedRows = ({
  gridApi,
  selectedRowIds,
  tableId,
}: QuickActionRunSelectedRowsProps) => {
  const validRowIds = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    const rows = table?.rows.filter(({ id }) => selectedRowIds.includes(id));

    if (table === undefined) {
      return [];
    }

    return (rows ?? [])
      .filter((row) => {
        return (
          row.task === null &&
          row.toolVersion &&
          row.toolVersion.status !== ToolStatus.DEPRECATED &&
          row.cells.every((cell, colIdx) => {
            const column = table.columns[colIdx];
            const errors = getValidationErrors({
              tableKind: table.kind,
              columnDefinition: column.definition,
              isColumnRequired: column.required,
              value: cell.value,
            });
            const blockingErrors = errors.filter((e) => e.isBlocking);
            return blockingErrors.length === 0;
          })
        );
      })
      .map((row) => row.id);
  });

  return (
    <QuickActionRunRowBase
      gridApi={gridApi}
      tooltipContent={[`Run selected rows: ${validRowIds.length}`]}
      tableId={tableId}
      rowIds={validRowIds}
      isDisabled={validRowIds.length === 0}
      id={"quickActionRunSelectedRows"}
      requireConfirmation
    />
  );
};
